import { useEffect } from 'react';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils';
import { PlayerTrackInfoDocument, useQuery } from '@graphql/reactQuery';
import { useReleaseTrackAudioInfo } from '@hooks/useTrackAudioInfo';
import { removeTrackById } from '@store/audio/AudioQueue';

/*#__PURE__*/ gql(/* GraphQL */ `
  query PlayerTrackInfo($trackId: UUID!) {
    track(id: $trackId) {
      id
      ...TrackAudioInfo

      release {
        id
        listeningParty
        ...ReleaseTrackAudioInfo
        ...FeedPostModalMainSound
      }
    }
  }
`);

export function usePlayerTrackInfo({
  trackId,
  shouldFetch,
}: {
  trackId: Maybe<string>;
  shouldFetch: boolean;
}) {
  const { data, isFetched } = useQuery(PlayerTrackInfoDocument, {
    variables: !!trackId && {
      trackId,
    },
    // cacheTime: Infinity means that this info should never be garbage collected
    cacheTime: Infinity,
    // staleTime: Infinity means that this info is never considered stale and doesn't need to be revalidated
    staleTime: Infinity,
    enabled: shouldFetch,
  });

  useEffect(() => {
    // track is not playable so remove it from queue
    if (trackId && isFetched && !data?.data.track) removeTrackById(trackId);
  }, [trackId, isFetched, data?.data.track]);

  const { releaseInfo, trackInfoAudio, trackAudioUrl, feedPostInfo } = useReleaseTrackAudioInfo({
    track: data?.data.track,
    release: data?.data.track?.release,
  });

  return {
    releaseInfo,
    feedPostInfo,
    trackInfoAudio,
    trackAudioUrl,
  };
}
